import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getBSectionVO,
    saveBSectionPage,
} from '@/services/auditService/auditProgramService';
import {
    B1_CONTENT,
    B_CHECKLIST_PAGES,
    B_PAGE_TITLES,
    B_SECTION_INDEX,
    B_SECTION_INDEX_LETTER,
    B_SECTION_INDEX_TITLE,
    B_SECTION_PAGES,
    BChecklistPage,
    BChecklistPageCode,
    BSectionPageCode,
    syncBDatesFromB1PartnerDate,
    withB1Defaults,
    withB1PartnerDates,
    withB61Defaults,
    withB62Defaults,
    withB7Defaults,
    withBChecklistDefaults,
    withCodePropKinds,
} from '@/pages/components/audit-program/b-section/constants';
import B1MemorandumView from '@/pages/components/audit-program/b-section/B1MemorandumView';
import B61SignificantMattersView from '@/pages/components/audit-program/b-section/B61SignificantMattersView';
import B62EvaluationView from '@/pages/components/audit-program/b-section/B62EvaluationView';
import B7UnadjustedMisstatementsView from '@/pages/components/audit-program/b-section/B7UnadjustedMisstatementsView';
import BChecklistView from '@/pages/components/audit-program/b-section/BChecklistView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import SectionIndexView from '@/pages/components/audit-program/SectionIndexView';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type BSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: BSectionPageCode;
};

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function mergeB1HeaderFromApi(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        partnerName: page.partnerName || data.partnerName || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
    };
}

function withChecklistDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    const withSample = withBChecklistDefaults(refCode, page);
    return {
        ...withSample,
        client: withSample.client || data.client || '',
        yearEnd: withSample.yearEnd || data.yearEnd || '',
        fileNo: withSample.fileNo || data.fileNo || '',
        preparedBy: withSample.preparedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: withSample.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: withSample.ref || refCode,
    };
}

export default function BSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'Bi',
}: BSectionViewProps) {
    const [pageKey, setPageKey] = useState<BSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('B');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getBSectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of B_SECTION_PAGES) {
                const raw = toStringRecord(data.pages?.[code]);
                if (code === 'Bi') {
                    pages[code] = raw;
                } else if (code === 'B1') {
                    const b1Template = getContent('B1') as {followUpSections?: Array<{key?: string; defaultText?: string}>} | undefined;
                    pages[code] = withB1Defaults(mergeB1HeaderFromApi(raw, data), b1Template);
                } else if (code === 'B6.1') {
                    pages[code] = withB61Defaults(withChecklistDefaults(raw, data, code));
                } else if (code === 'B6.2') {
                    pages[code] = withB62Defaults(withChecklistDefaults(raw, data, code));
                } else if (code === 'B7') {
                    pages[code] = withB7Defaults(withChecklistDefaults(raw, data, code));
                } else {
                    pages[code] = withChecklistDefaults(raw, data, code);
                }
            }
            const b1PartnerDate = pages.B1?.partnerDate;
            for (const code of B_SECTION_PAGES) {
                if (code === 'B1' || code === 'Bi') continue;
                pages[code] = withB1PartnerDates(pages[code], b1PartnerDate);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load B Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName, getContent]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: BSectionPageCode, key: string, value: string) => {
        setPageData((prev) => {
            const nextPage = {...(prev[pageCode] ?? {}), [key]: value};
            if (pageCode === 'B1' && key === 'partnerDate') {
                const prevPartnerDate = prev.B1?.partnerDate;
                const next: Record<string, Record<string, string>> = {...prev, B1: nextPage};
                for (const code of B_SECTION_PAGES) {
                    if (code === 'B1' || code === 'Bi') continue;
                    next[code] = syncBDatesFromB1PartnerDate(prev[code] ?? {}, value, prevPartnerDate);
                }
                return next;
            }
            return {...prev, [pageCode]: nextPage};
        });
        setDirty((prev) => {
            if (pageCode === 'B1' && key === 'partnerDate') {
                const next: Record<string, boolean> = {...prev, B1: true};
                for (const code of B_SECTION_PAGES) {
                    if (code !== 'B1' && code !== 'Bi') next[code] = true;
                }
                return next;
            }
            return {...prev, [pageCode]: true};
        });
    }, []);

    const handleSave = useCallback(async () => {
        if (pageKey === 'Bi' || !dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveBSectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const tabs = useMemo(() => B_SECTION_PAGES.map((code) => {
        const templatePage = getContent(code);
        return {
        key: code,
        label: <PageTabLabel code={code} title={B_PAGE_TITLES[code]}/>,
        icon: code,
        content: header ? (
            code === 'Bi' ? (
                <SectionIndexView
                    header={header}
                    refCode="Bi"
                    sectionLetter={B_SECTION_INDEX_LETTER}
                    sectionTitle={B_SECTION_INDEX_TITLE}
                    rows={B_SECTION_INDEX}
                    onNavigate={(pageCode) => setPageKey(pageCode as BSectionPageCode)}
                />
            ) : code === 'B1' ? (
                <B1MemorandumView
                    header={header}
                    values={pageData.B1 ?? {}}
                    onChange={(k, v) => handleFieldChange('B1', k, v)}
                    content={(templatePage ?? B1_CONTENT) as typeof B1_CONTENT}
                />
            ) : code === 'B6.1' ? (
                <B61SignificantMattersView
                    header={header}
                    values={pageData['B6.1'] ?? {}}
                    onChange={(k, v) => handleFieldChange('B6.1', k, v)}
                />
            ) : code === 'B6.2' ? (
                <B62EvaluationView
                    header={header}
                    values={pageData['B6.2'] ?? {}}
                    onChange={(k, v) => handleFieldChange('B6.2', k, v)}
                />
            ) : code === 'B7' ? (
                <B7UnadjustedMisstatementsView
                    header={header}
                    values={pageData.B7 ?? {}}
                    onChange={(k, v) => handleFieldChange('B7', k, v)}
                />
            ) : (
                <BChecklistView
                    header={header}
                    page={withCodePropKinds(
                        (templatePage ?? B_CHECKLIST_PAGES[code as BChecklistPageCode]) as BChecklistPage,
                        B_CHECKLIST_PAGES[code as BChecklistPageCode],
                    )}
                    refCode={code}
                    values={pageData[code] ?? {}}
                    onChange={(k, v) => handleFieldChange(code, k, v)}
                />
            )
        ) : null,
    };
    }), [header, pageData, handleFieldChange, getContent]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    B Section — Audit Completion
                </Typography>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon/>}
                    disabled={pageKey === 'Bi' || !dirty[pageKey] || saving}
                    onClick={() => void handleSave()}
                >
                    {pageKey === 'Bi' ? 'Display only' : `Save ${pageKey}`}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" sx={{px: 2, pb: 1}} variant="body2">{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as BSectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    leftPaneSx={{minWidth: 180}}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
